import 'leaflet/dist/leaflet.css';
import { useEffect, useRef, useState } from 'react';
import { FlightsWithVersionInfos } from '../models/flightswithversioninfos.model';
import manifestV1Service from '../services/manifest-v1.service';
import './flights-view.component.css';
import { FlightWithVersion } from '../models/flightwithversion.model';
import moment from 'moment';
import { FlightLoad } from '../models/flightload.model';
import { Flight } from '../models/flight.model';
import { Plane } from '../models/plane.model';
import '../shared/constants';
import Constants from '../shared/constants';
import { WorkType } from '../models/worktype.model';
import { Person } from '../models/person.model';
import { Tandem } from '../models/tandem.model';
 
interface FlightsViewProps {
  withTandems:boolean;
}

function FlightsView(props:FlightsViewProps) {
  const firstUpdate = useRef(true); 
  const [planes, setPlanes] = useState<Plane[]>([]); 
  const [workTypes, setWorkTypes] = useState<WorkType[]>([]); 
  const [model, setModel] = useState<FlightWithVersion[]>([]); 
  const [tandems, setTandems] = useState<Tandem[]>([]);
  const [errorMsg, setErrorMsg] = useState(''); 
  const [error, setError] = useState(false); 
  const pilots = useRef<Person[]>([]); 
  const teams = useRef<Person[]>([]);  
  const loadDataTimeoutRef = useRef<NodeJS.Timeout|null>(null);

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      loadData();
    }
    
    return () => {
      console.log("cleaning");
      if (loadDataTimeoutRef.current != null) {
        clearTimeout(loadDataTimeoutRef.current);
      }
    };
  }, []);


  async function loadData() {
    // Set up an interval to call loadData() every 10 seconds
    if (loadDataTimeoutRef.current != null) {
      clearTimeout(loadDataTimeoutRef.current);
    }

    // Load pilots, jump masters, teams once
    if (pilots.current.length == 0) {
      pilots.current = await manifestV1Service.getPilots();
    }
    if (teams.current.length == 0) {
      teams.current = await manifestV1Service.getTeams();
    }

    try {
      const data = await manifestV1Service.getFlights();
      setPlanes(data.planes != null ? data.planes : []);
      setWorkTypes(data.workTypes != null ? data.workTypes : []);

      const filtered = filterSortData(data);
      setModel(filtered);
      setError(false);
    } catch (err) {
      setErrorMsg('' + err);
      setError(true);
    }

    if (props.withTandems) {
      try {
        const data = await manifestV1Service.getTandems();
        setTandems(data);
      } catch (err) {
        setTandems([]);
      }
    }
    
    loadDataTimeoutRef.current = setTimeout(loadData, 15000);
  }

  function getPilotName(id: number): string {
    for (let i = 0; i < pilots.current.length; i++) {
      if (pilots.current[i].id == id) {
        return pilots.current[i].firstName + " " + pilots.current[i].lastName;
      }
    }
    return "" + id;
  }

  function getTeamName(id: number): string {
    for (let i = 0; i < teams.current.length; i++) {
      if (teams.current[i].id == id) {
        return "" + teams.current[i].firstName;
      }
    }
    return "" + id;
  }

  function filterSortData(data: FlightsWithVersionInfos): FlightWithVersion[] {
    // for each plane: keep last took off + 2 next scheduled
    const perPlane: any = {};
    data?.flights?.forEach(f => {
      if (f.flight != null && f.flight.planeId != null) {
        let found = perPlane[f.flight.planeId];
        if (found == null) {
          found = {};
          found.lastTookOff = f.flight.status === Constants.DEPARTED ? f : null;
          found.nextScheduled = [];
          if (f.flight.status === Constants.SCHEDULED || f.flight.status === Constants.STAND_BY) {
            found.nextScheduled.push(f);
          }
          perPlane[f.flight.planeId] = found;
        } else {
          if (f.flight.status === Constants.SCHEDULED || f.flight.status === Constants.STAND_BY) {
            found.nextScheduled.push(f);
          }
          else if (f.flight.status === Constants.DEPARTED) {
            if (found.lastTookOff == null) {
              found.lastTookOff = f;
            } else {
              if (f.flight.departureTime && new Date(f.flight.departureTime).getTime() > new Date(found.lastTookOff.flight.departureTime).getTime()) {
                found.lastTookOff = f;
              }
            }
          }
        }
      }
    });

    let filtered: FlightWithVersion[] = [];
    for (const planeId in perPlane) {
      if (perPlane[planeId].lastTookOff != null) {
        filtered.push(perPlane[planeId].lastTookOff);
      }

      if (perPlane[planeId].nextScheduled) {
        perPlane[planeId].nextScheduled.sort((f1: Flight, f2: Flight) => {
          return (f1.stickNo ? f1.stickNo : 0) - (f2.stickNo ? f2.stickNo : 0);
        });
        filtered = filtered.concat(perPlane[planeId].nextScheduled);
      }
    }

    // Sort on departure time
    filtered.sort((fwv1, fwv2) => {
      if (fwv1.flight == null || fwv1.flight.departureTime == null) {
        return -1;
      }
      if (fwv2.flight == null || fwv2.flight.departureTime == null) {
        return -1;
      }
      return new Date(fwv1.flight.departureTime).getTime() - new Date(fwv2.flight?.departureTime).getTime();
    });

    // Mark jump masters & B2B
    filtered.forEach(fwv => {
      if (fwv.flight && fwv.flight.flightLoadsSorted && fwv.flight.flightLoadsSorted.length > 0) {
        fwv.flight.flightLoadsSorted.forEach(fl => {
          if (fwv.flight?.jumpMasterId === fl.personId) {
            fl.isJumpMaster = true;
          }

          fl.b2b = isBackToBack(fl, fwv.flight as Flight, data != null && data.flights != null ? data.flights : []);
          
          if (fl.personId!=null) {
            fl.balanceWarning = balanceWarning(data.skydiversIdsBalanceWarning!=null?data.skydiversIdsBalanceWarning:[], fl.personId);
          }
        });
      } else {
        console.log('no load for ' + fwv.flight?.planeId + fwv.flight?.stickNo);
      }
    });

    return filtered;
  }

  function isBackToBack(target:FlightLoad, f:Flight, flights:FlightWithVersion[]) {
    if (target.personId == null || target.personId === 0) {
      return false;
    }
    const previousFlightStickNo = (f.stickNo?f.stickNo:0) - 1;
    const previousFlightPlane = f.planeId;
    const previousFlight = flights.find(fwv => {
      if(fwv.flight && fwv.flight.flightLoadsSorted) {
        if(fwv.flight.stickNo === previousFlightStickNo && fwv.flight.planeId === previousFlightPlane) {
          return true;
        }
      }
      return false;
    });

    if(previousFlight) {
      if(previousFlight.flight?.refuelAfterLoad === true) {
        return false;
      }
      const found = previousFlight.flight?.flightLoadsSorted?.filter(fl => {
        return fl.personId === target.personId;
      });
      return found && found.length > 0;
    }
    return false;
  }

  function getPersonRowColor(fl:FlightLoad, f:Flight|undefined, workTypes:WorkType[]): string {
    if(f==null) {
      return "#FFFFFF";
    }
    
    if(fl!=null) {
      if(fl.personName==Constants.AVAILABLE) {
        return "#444444";
      }
    }
    if(f!=null && fl.workType!=null) {					
      for(let i=0;i<workTypes.length;i++) {
        if(workTypes[i].code===fl.workType) {
          const col = workTypes[i].color;
          if(col==null || col=="0") {
            return "#FFFFFF";
          }  
          let hexCol = parseInt(col).toString(16);
          while(hexCol.length < 6) {
            hexCol = '0'+hexCol;
          }
          return "#"+hexCol;
        }
      } 
    }
    return "#FFFFFF";
  }

   function getJumpShortLib(f:FlightLoad, workTypes:WorkType[]):string {
    if(f.teamId != 0) {
      return "";
    }

    if(f!=null && f.workType!=null) {					
      for(let i=0;i<workTypes.length;i++) {
        if(workTypes[i].code===f.workType) {
          return workTypes[i].shortLib!=null && workTypes[i].shortLib!=""?workTypes[i].shortLib+" ":"";
        }
      } 
    }
    return "";
  }
  
  /***
   * Regroup attached loads
   */
  function getMarginTop(f:FlightLoad, seatNo:number):string {  
    if(seatNo==1) {
      return '0px';
    }
    if(f!=null){
      if(f.rootFlightLoad!=null && f.rootFlightLoad!=0) {
        return '0px';
      } else {
        return '12px';
      }
    }
    return '12px';
  }
  
  function getPersonName(l:FlightLoad, flight:Flight|undefined) :String {
    if(flight==null) {
      return "";
    }
    if( flight.autoFillValidated==false && (
      l.autofillStatus==Constants.AUTO_FILL_DONE ||
      l.autofillStatus==Constants.AUTO_FILL_POPUP_CONFIRMED
      )
      ) {
      return "";
    }
    if(l.teamId == 0) {
      return l.personName!=null?l.personName:"";
    } else {
        return (l.personName!=null?l.personName:"") + " [" + getTeamName(l.teamId!=null?l.teamId:0) + "]";     
    }
  }
  
  function getPersonsBgColor(f:Flight, planes:Plane[]): string {					
    if(f.status==Constants.DEPARTED) {
      return Constants.DEPARTED_COLOR_LIST;
    }
    
    if(f.status==Constants.STAND_BY) {
      return Constants.STAND_BY_COLOR_LIST;
    }
     
    return "#000000";
  }

  function getBgColor(f:Flight, planes:Plane[]): string {					
    if(f.status==Constants.DEPARTED) {
      return Constants.DEPARTED_COLOR;
    }
    
    if(f.status==Constants.STAND_BY) {
      return Constants.STAND_BY_COLOR;
    }
    
    let plane:Plane = new Plane();
    plane.planeColor = "0";
    planes.forEach( p => {
        if(p.planeId === f.planeId) {
          plane = p;
        }
      }); 

    return "#"+parseInt(plane.planeColor).toString(16);
  }

  function getTimeStatusLib(f:Flight):string {
    if(f.status==Constants.STAND_BY) {
      return "";
    }
    if(f.departureTime==null) {
      return "";
    }

    const now = new Date(); 
    const diffMs = new Date(f.departureTime).getTime() - now.getTime();
    const diffMins = Math.round(diffMs/1000/60);

    if(diffMins > 0) {
      if(f.status!=Constants.DEPARTED) {
        return diffMins + " min remaining";
      }
      return ""; // should not happen
    } else {
      return "--";
      /*
      if(f.status==Constants.DEPARTED) {
        return Math.abs(diffMins) + " min ago";
      } else {
        return Math.abs(diffMins) + " min late";
      }*/
    }
  }

  function getColumnFooter1(f:Flight, flights:FlightWithVersion[]) : string {
    if(f==null) {
      return "";
    } 

    // Refuel 
    let refuelInPreviousFlight = false;
    const stickNo = f.stickNo!=null?f.stickNo:-1;

    flights.forEach( fl => {
      if(fl.flight!=null) {
        if(fl.flight.planeId == f.planeId) {
            if(fl.flight.refuelAfterLoad == true && fl.flight.stickNo == stickNo - 1 ) {              
                refuelInPreviousFlight = true;
            }
          }
        }
      });

    if(refuelInPreviousFlight) {
      return "REFUEL BEFORE";
    }
    return "";
  }
  
  /***
   * Flight Footer label2
   * 
   */
  function getColumnFooter2(f:Flight) : string {
    if(f==null) {
      return "";
    }
    
    var label:string = '';
    if(f.planeIsFull==true && f.status!=Constants.DEPARTED) {
      label = "NO MORE PLACE";
    } 
    if(f.status==Constants.DEPARTED) {
      label = "DEPARTED";
    }
    return label;
  }
  
  /***
   * Flight Footer label2
   * 
   */
  function getColumnFooter3(f:Flight) : string {
    if(f==null) {
      return "";
    }
    
    if(f.minJumps!=null && f.minJumps>0) {
      return "MIN "+f.minJumps+" JUMPS";
    }
    return "";
  }

  function refresh() {
    window.location.reload();
  }

  function getFillGroupName(fillGroup: number) {
    if (fillGroup == null) {
      return "";
    }
    if (fillGroup === 0x000000) {
      return ""; // (inverted)
    }
    if (fillGroup === 0xFFFFFF) {
      return "B0"; // (inverted)
    }
    if (fillGroup === 0xFF0000) {
      return "R1";
    }
    if (fillGroup === 0x00FF00) {
      return "G1";
    }
    if (fillGroup === 0x0000FF) {
      return "B2";
    }
    if (fillGroup === 0x3c3c3c) {
      return "G2";
    }
    if (fillGroup === 0xFF00FF) {
      return "P1";
    }
    if (fillGroup === 0x00FFFF) {
      return "B3";
    }
    if (fillGroup === 0xFFFF00) {
      return "Y1";
    }
    if (fillGroup === 0x3F4CDD) {
      return "B1";
    }
    if (fillGroup === 0x6600AA) {
      return "P2";
    }
    return "X" + (fillGroup % 10) + "";
  }

  function balanceWarning(skydiversIdsBalanceWarning:number[], personId:number) {
    return skydiversIdsBalanceWarning.includes(personId);
  }
  
  return (
    error ? <div style={{color:"red", fontSize:"20px", textAlign: "center"}}>
              <div className="red-message-top">{errorMsg}</div>
              <input type="button" 
                             className="open-close-dropzone-button"
                             onClick={refresh} 
                             value={"Reload page"}/>
            </div> 
      :   
      <>
        <div className='flights-view'>
            <div className="flights-list" >
            {model?.map((fwv: FlightWithVersion) => (
                <div className="flight">
                    {fwv.flight!=null?
                     <>
                      <div className="flight-header" style={{backgroundColor: getBgColor(fwv.flight, planes)}}>
                          <div> 
                              {fwv.flight.planeId} N°{fwv.flight.stickNo} - {fwv.flight.departureTime ? moment(fwv.flight.departureTime).format('HH:mm') : '??:??'}
                          </div>
                          <div>
                            {getTimeStatusLib(fwv.flight)}
                          </div>
                          <div style={{borderTop: '1px solid white', marginTop:'3px', marginBottom: '3px', fontSize: '15px'}}>
                            <div>{fwv.flight.pilotId!=null && fwv.flight.pilotId!=0 ? getPilotName(fwv.flight.pilotId): ""}</div>
                            <div>{fwv.flight.copilotId!=null && fwv.flight.copilotId!=0 ? getPilotName(fwv.flight.copilotId): ""} </div>
                          </div>
                      </div>

                      <div className="flight-persons" style={{  backgroundColor: getPersonsBgColor(fwv.flight, planes)}}>
                          {fwv.flight.flightLoadsSorted?.map((fl: FlightLoad, seatNo:number, arr:FlightLoad[]) => (
                              <div style={{
                                            fontSize: '14px',
                                            marginTop: getMarginTop(fl, seatNo+1),
                                            padding: '2px',
                                            color: getPersonRowColor(fl, fwv.flight, workTypes),
                                            backgroundColor:  "transparent", 
                                            cursor: fl.canBeJumpMaster ? 'pointer' : '',
                                            display: 'flex',
                                            flexDirection: 'row',
                                            justifyContent: 'space-between'
                                          }}
                              > 
                                <div>
                                    {seatNo+1}.                                 
                                    {getPersonName(fl, fwv.flight)}
                                    {((!fl.flightLoads || fl.flightLoads.length===0) && !fl.rootFlightLoad && fl.fillGroup)? <span className={"fill-group fill-group-" + fl.fillGroup.toString(16).padStart(6, '0').toUpperCase()}>
                                      {getFillGroupName(fl.fillGroup)}
                                      </span>:null}
                                    {fl.balanceWarning ?<span className="balance-warning">€</span>:null}
                                    {fl.b2b?<span className="back-to-back">B2B</span>:null}
                                    {fl.isJumpMaster?<span className='jump-master'>JM</span>:null}
                                </div>
                                
                                <div>{getJumpShortLib(fl, workTypes)}</div>
                              </div>
                          ))}
                      </div>

                      <div className="flight-footer" style={{backgroundColor: getBgColor(fwv.flight, planes)}}>
                          <div> 
                            {getColumnFooter1(fwv.flight, model)}
                          </div>  
                          <div>
                            {getColumnFooter2(fwv.flight)}
                          </div>
                          <div> 
                            {getColumnFooter3(fwv.flight)}
                          </div>  
                      </div> 
                    </>
                    :null}
                    
                </div>
            ))}
            </div>
            {tandems && tandems.length>0 ? <div className="tandem-list">
              {tandems?.map((tandem:Tandem)=> (
                <div className="tandem-row">
                  <div>{tandem.tandemPassenger?.id}</div>
                  <div>{tandem.tandemPassenger?.firstName}</div>
                  <div>{tandem.tandemPassenger?.lastName}</div>
                </div>
              ))}

            </div>: null}
        </div>
      </>
  )
}

export default FlightsView; 
